const CUSTOM_ID_RE = /^[0-9a-f]{32}$/;

export function isCustomCardId(id: string): boolean {
  return CUSTOM_ID_RE.test(id);
}

export function buildShareUrl(cardId: string, variant?: number): string {
  const suffix = variant !== undefined ? `-${variant}` : '';
  return `${window.location.origin}/card/${cardId}${suffix}`;
}

let activeToast: HTMLElement | null = null;

export function showToast(message: string, kind: 'success' | 'error' = 'success'): void {
  if (activeToast) activeToast.remove();
  const el = document.createElement('div');
  el.className = `toast toast-${kind}`;
  el.textContent = message;
  document.body.appendChild(el);
  activeToast = el;
  setTimeout(() => el.classList.add('fade-out'), 2200);
  setTimeout(() => {
    if (activeToast === el) {
      el.remove();
      activeToast = null;
    }
  }, 2700);
}

/** iOS-style share glyph: a box open at the top with an up-arrow rising
 *  through the opening. Single filled path — uses `currentColor` so the
 *  button's text color drives the icon fill, keeping it themable via the
 *  surrounding `.share-btn` rules. */
const SHARE_ICON_SVG = `<svg class="share-icon-svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"><path d="M11.293 2.293a1 1 0 0 1 1.414 0l3 3a1 1 0 0 1-1.414 1.414L13 5.414V15a1 1 0 1 1-2 0V5.414L9.707 6.707a1 1 0 0 1-1.414-1.414l3-3zM4 11a2 2 0 0 1 2-2h2a1 1 0 0 1 0 2H6v9h12v-9h-2a1 1 0 1 1 0-2h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-9z"/></svg>`;

/** Open the OS share sheet (mobile) or desktop share dialog when supported,
 *  falling back to clipboard copy. User-canceled shares are silent.
 *  Browsers without `navigator.share` (most desktops) take the clipboard
 *  path and show the existing "Share link copied" toast. */
export async function shareLink(cardId: string, variant?: number): Promise<void> {
  const url = buildShareUrl(cardId, variant);
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ url });
      return;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // Other errors fall through to the clipboard path below.
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    showToast('Share link copied', 'success');
  } catch {
    showToast(`Copy failed. URL: ${url}`, 'error');
  }
}

export interface ShareButtonOptions {
  compact?: boolean;
  getVariant?: () => number | undefined;
}

export function createShareButton(cardId: string, opts: ShareButtonOptions = {}): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = opts.compact ? 'share-btn compact' : 'share-btn';
  btn.title = 'Share';
  btn.setAttribute('aria-label', 'Share');
  btn.innerHTML = opts.compact
    ? `<span class="share-btn-icon">${SHARE_ICON_SVG}</span>`
    : `<span class="share-btn-icon">${SHARE_ICON_SVG}</span><span class="share-btn-label">Share</span>`;
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    shareLink(cardId, opts.getVariant?.());
  });
  return btn;
}
